import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { publicApi } from '@/lib/api';

const projectTypes = [
  { value: '', label: 'All Projects' },
  { value: 'residential', label: 'Residential' },
  { value: 'social_housing', label: 'Social Housing' },
];

const statusColors = {
  completed: 'bg-green-100 text-green-800',
  ongoing: 'bg-amber-100 text-amber-800',
  upcoming: 'bg-blue-100 text-blue-800',
};

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('');
  
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const params = activeFilter ? { project_type: activeFilter } : {};
        const response = await publicApi.getProjects(params);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProjects();
  }, [activeFilter]);
  
  return (
    <div className="pt-20" data-testid="projects-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32" data-testid="projects-hero">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <p className="text-muted-foreground text-sm tracking-[0.2em] uppercase mb-4">
              Our Portfolio
            </p>
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] mb-6">
              Projects
            </h1>
            <p className="text-muted-foreground text-lg leading-relaxed">
              Explore our portfolio of property developments across Manawatu. 
              From residential subdivisions to commercial projects, each development 
              reflects our commitment to quality and community.
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Filter Section */}
      <section className="pb-12" data-testid="filter-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-muted-foreground text-sm flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filter:
            </span>
            {projectTypes.map((type) => (
              <Button
                key={type.value}
                variant={activeFilter === type.value ? 'default' : 'outline'}
                size="sm"
                onClick={() => setActiveFilter(type.value)}
                className="rounded-full"
                data-testid={`filter-${type.label.toLowerCase().replace(' ', '-')}`}
              >
                {type.label}
              </Button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Projects Grid */}
      <section className="pb-20 md:pb-32" data-testid="projects-grid">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="skeleton h-96 rounded-lg" />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-muted-foreground text-lg">No projects found.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="group"
                >
                  <Link 
                    to={`/projects/${project.id}`} 
                    className="block bg-white border border-border/40 hover:border-black/20 transition-colors duration-300"
                    data-testid={`project-card-${index}`}
                  >
                    {/* Image */}
                    <div className="relative overflow-hidden aspect-[4/3]">
                      <img 
                        src={project.images?.[project.images.length - 1]?.url || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'} 
                        alt={project.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute top-4 left-4 flex gap-2">
                        <span className={`px-3 py-1 text-xs font-medium uppercase tracking-wider rounded-full ${statusColors[project.status]}`}>
                          {project.status}
                        </span>
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-6">
                      <span className="text-muted-foreground text-xs uppercase tracking-wider">
                        {(project.project_type || '').replace(/_/g, ' ')}
                      </span>
                      <h3 className="text-xl font-semibold mt-2 mb-2 group-hover:text-muted-foreground transition-colors">
                        {project.name}
                      </h3>
                      <div className="flex items-center text-muted-foreground text-sm gap-1 mb-4">
                        <MapPin className="w-4 h-4" />
                        {project.location}
                      </div>
                      <p className="text-muted-foreground text-sm leading-relaxed line-clamp-2">
                        {project.description}
                      </p>
                      
                      {/* View Project Link */}
                      <div className="mt-4 flex items-center text-sm font-medium group-hover:gap-2 transition-all">
                        View Project
                        <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-secondary/50" data-testid="projects-cta">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6">
              Have a Project in Mind?
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-10">
              Let's discuss how Koro Living can help bring your development vision to life.
            </p>
            <Button asChild size="lg" className="rounded-full px-10 py-6" data-testid="enquire-cta">
              <Link to="/contact">
                Enquire About Developments
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
